import { useState } from 'react';
import Input from './ui/Input';
import Textarea from './ui/Textarea';
import Select from './ui/Select';
import Button from './ui/Button';
import ImagePreview from './ui/ImagePreview';
import AssetField from './AssetField';
import { translateText } from '../../services/adminApi';

const emptyListItem = (itemType) => (itemType === 'bilingual' ? { fr: '', en: '' } : '');

function TranslateButton({ sourceText, onTranslated }) {
  const [translating, setTranslating] = useState(false);
  const [error, setError] = useState(null);

  const handleClick = async () => {
    if (!sourceText?.trim()) return;
    setError(null);
    setTranslating(true);
    try {
      const { translatedText } = await translateText(sourceText, 'fr', 'en');
      onTranslated(translatedText);
    } catch (err) {
      setError(err.message || 'Traduction indisponible, réessaie.');
    } finally {
      setTranslating(false);
    }
  };

  return (
    <div className="admin-translate-control">
      <Button type="button" variant="secondary" onClick={handleClick} disabled={translating}>
        {translating ? 'Traduction...' : 'Traduire → EN'}
      </Button>
      {error && <span className="admin-translate-error">{error}</span>}
    </div>
  );
}

function BilingualPair({ field, value, onChange, error }) {
  const Field = field.as === 'textarea' ? Textarea : Input;
  return (
    <div className="admin-bilingual-pair">
      <div className="admin-form-inline">
        <Field
          label={`${field.label} FR`}
          value={value?.fr || ''}
          onChange={(e) => onChange({ ...value, fr: e.target.value })}
          error={error?.fr}
        />
        <Field
          label={`${field.label} EN`}
          value={value?.en || ''}
          onChange={(e) => onChange({ ...value, en: e.target.value })}
          error={error?.en}
        />
      </div>
      <TranslateButton sourceText={value?.fr} onTranslated={(en) => onChange({ ...value, en })} />
    </div>
  );
}

function ListField({ field, value, onChange, onUpload }) {
  const items = value || [];
  const updateItem = (index, next) => onChange(items.map((item, i) => (i === index ? next : item)));
  const removeItem = (index) => onChange(items.filter((_, i) => i !== index));
  const addItem = () => onChange([...items, emptyListItem(field.itemType)]);

  if (field.itemType === 'asset') {
    const moveItem = (index, direction) => {
      const swapIndex = direction === 'up' ? index - 1 : index + 1;
      if (swapIndex < 0 || swapIndex >= items.length) return;
      const next = [...items];
      [next[index], next[swapIndex]] = [next[swapIndex], next[index]];
      onChange(next);
    };

    return (
      <div className="admin-field">
        <span>{field.label}</span>
        {items.length > 0 && (
          <div className="admin-image-thumbs">
            {items.map((item, index) => (
              <div className="admin-image-thumb-item" key={`${item}-${index}`}>
                <ImagePreview src={item} size={48} />
                <div className="admin-list-item-actions">
                  <button
                    type="button"
                    className="admin-icon-btn"
                    onClick={() => moveItem(index, 'up')}
                    disabled={index === 0}
                    title="Monter"
                  >
                    ↑
                  </button>
                  <button
                    type="button"
                    className="admin-icon-btn"
                    onClick={() => moveItem(index, 'down')}
                    disabled={index === items.length - 1}
                    title="Descendre"
                  >
                    ↓
                  </button>
                  <button
                    type="button"
                    className="admin-icon-btn admin-icon-btn-danger"
                    onClick={() => removeItem(index)}
                    title="Supprimer"
                  >
                    ×
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        <input
          type="file"
          accept="image/*"
          multiple
          onChange={async (e) => {
            const files = Array.from(e.target.files || []);
            e.target.value = '';
            if (!files.length || !onUpload) return;
            onChange([...items, ...(await Promise.all(files.map(onUpload)))]);
          }}
        />
      </div>
    );
  }

  return (
    <div className="admin-field">
      <span>{field.label}</span>
      {items.map((item, index) => (
        <div className="admin-list-item" key={index}>
          <div className="admin-form-inline">
            <Input
              placeholder={`${field.label} FR`}
              value={item.fr || ''}
              onChange={(e) => updateItem(index, { ...item, fr: e.target.value })}
            />
            <Input
              placeholder={`${field.label} EN`}
              value={item.en || ''}
              onChange={(e) => updateItem(index, { ...item, en: e.target.value })}
            />
          </div>
          <div className="admin-list-item-actions">
            <TranslateButton sourceText={item.fr} onTranslated={(en) => updateItem(index, { ...item, en })} />
            <button
              type="button"
              className="admin-icon-btn admin-icon-btn-danger"
              onClick={() => removeItem(index)}
              title="Supprimer"
            >
              ×
            </button>
          </div>
        </div>
      ))}
      <Button variant="secondary" onClick={addItem}>
        + Ajouter
      </Button>
    </div>
  );
}

function MultiSelectField({ field, value, onChange }) {
  const selected = value || [];
  const toggle = (optValue) => {
    onChange(selected.includes(optValue) ? selected.filter((v) => v !== optValue) : [...selected, optValue]);
  };
  return (
    <div className="admin-field">
      <span>{field.label}</span>
      <div className="admin-multiselect">
        {field.options.map((opt) => (
          <label key={opt.value}>
            <input type="checkbox" checked={selected.includes(opt.value)} onChange={() => toggle(opt.value)} />
            {opt.icon && <ImagePreview src={opt.icon} size={18} />}
            {opt.label}
          </label>
        ))}
      </div>
    </div>
  );
}

const validateFields = (fields, values) => {
  const errors = {};
  fields.forEach((field) => {
    if (!field.required) return;
    if (field.type === 'bilingual') {
      const value = values[field.name] || {};
      if (!value.fr || !value.en) {
        errors[field.name] = { fr: value.fr ? undefined : 'Requis', en: value.en ? undefined : 'Requis' };
      }
    } else if (!values[field.name]) {
      errors[field.name] = 'Requis';
    }
  });
  return errors;
};

function EntityForm({ fields, initialValues = {}, onSubmit, submitLabel = 'Enregistrer', onUpload }) {
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState({});

  const setField = (name, value) => setValues((prev) => ({ ...prev, [name]: value }));

  const handleSubmit = async () => {
    const nextErrors = validateFields(fields, values);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    await onSubmit(values);
  };

  return (
    <div className="admin-form">
      {fields.map((field) => {
        const error = errors[field.name];
        if (field.type === 'bilingual') {
          return (
            <BilingualPair
              key={field.name}
              field={field}
              value={values[field.name]}
              onChange={(v) => setField(field.name, v)}
              error={error}
            />
          );
        }
        if (field.type === 'textarea') {
          return (
            <Textarea
              key={field.name}
              label={field.label}
              placeholder={field.placeholder}
              value={values[field.name] || ''}
              onChange={(e) => setField(field.name, e.target.value)}
              error={error}
            />
          );
        }
        if (field.type === 'select') {
          if (field.multiple) {
            return (
              <MultiSelectField
                key={field.name}
                field={field}
                value={values[field.name]}
                onChange={(v) => setField(field.name, v)}
              />
            );
          }
          return (
            <Select
              key={field.name}
              label={field.label}
              value={values[field.name] || ''}
              onChange={(e) => setField(field.name, e.target.value)}
              options={field.options}
              error={error}
            />
          );
        }
        if (field.type === 'asset') {
          return (
            <AssetField
              key={field.name}
              label={field.label}
              value={values[field.name]}
              onChange={(v) => setField(field.name, v)}
            />
          );
        }
        if (field.type === 'list') {
          return (
            <ListField
              key={field.name}
              field={field}
              value={values[field.name]}
              onChange={(v) => setField(field.name, v)}
              onUpload={onUpload}
            />
          );
        }
        return (
          <Input
            key={field.name}
            label={field.label}
            placeholder={field.placeholder}
            value={values[field.name] || ''}
            onChange={(e) => setField(field.name, e.target.value)}
            error={error}
          />
        );
      })}
      <Button onClick={handleSubmit}>{submitLabel}</Button>
    </div>
  );
}

export default EntityForm;
